/** @module Auth0LoginHelper */
/* eslint-disable import/extensions */
import Auth0Constants from '../utils/auth0-constants.mjs';
import { resolveSsasBaseUrl } from '../utils/ssas-urls.mjs';
import Auth0Domain from '../utils/auth0-domain-utils.mjs';
import Auth0CookieHelper from '../utils/auth0-cookie-helper.mjs';
import Auth0Tenant from '../utils/auth0-tenant-utils.mjs';
import Auth0SupportedIdps from '../utils/auth0-supported-idps.mjs';
import Auth0UtilsBrowser from '../utils/auth0-utils-browser.mjs';
import Auth0DynamicScriptLoader from '../utils/auth0-dynamic-script-loader.mjs';
import Auth0SentryReporter from '../utils/auth0-sentry-reporter.mjs';
import Auth0Localizer from '../utils/auth0-localization-helper.mjs';
import Auth0WaitSpinner from './pages/auth0-wait-spinner-page-helper.mst.mjs';
import Auth0WhiteLabelAssetHelper from './utils/auth0-white-label-helper.mst.mjs';
import Auth0NotificationPageHelper from './pages/auth0-notification-page-helper.mst.mjs';
import Auth0StateHelper from '../utils/auth0-state-helper.mjs';
import Auth0PostLoginHandlerHelper from '../cdn/utils/post-login-handlers/auth0-post-login-handler-helper.mjs';
import Auth0PreLoginHandlerHelper from './utils/pre-login-handlers/auth0-pre-login-handler-helper.mjs';
import logger from '../utils/logging/auth0-logger.mjs';
import Auth0LocalStorageHelper from '../utils/auth0-local-storage-helper.mjs';
import AuthAnalytics from '../utils/auth-analytics.mjs';
import {
  EVENT_LOGIN_SUCCESS,
  STEP_LOGIN_PAGE,
  DEFAULT_AUTH_PROVIDER,
} from '../utils/auth-analytics-constants.mjs';
import { Auth0ProviderAdapter } from './utils/auth-adapters/auth0-provider-adapter.mjs';
import { ShipStationOIDCProviderAdapter } from './utils/auth-adapters/shipstation-oidc-provider-adapter.mjs';
import { PROVIDER_AUTH0, PROVIDER_SSAS } from './utils/auth-adapters/constants.mjs';

const externalScriptDependencies = [
  { url: Auth0Constants.AUTH0_SPAJS_URL, postLoad: () => { window.auth0SpaJs = window.auth0; } },
];

class Auth0LoginHelper {
  /**
   * @constructor
   * @description This is the primary entry point for external apps to start (and continue) the login flow. This script will handle all of the required login redirects, SSO linking, PKCE code exchanges and then final redirection to the SS Home partition. An application wishing to engage in using Auth0 in the SS-partitioned world should craft a simple page that dynamically loads this script via `script` element, instantiates an `Auth0LoginHelper` object and then calls `start()`. Because it is an ES6 module, you'll need to use the `import` directive in a `script` block of type `module`. Note that each tenant hosts its own `auth0-login-helper` package and, as such, the `import` directive must be dynamic.
   * @param {object} options Options object required to bootstrap the login helper
   * @param {string} options.domain The shipstation.com signin domain for the tenant (e.g. signin.shipstation.com, signin-stage.shipstation.com, etc)
   * @param {string} options.clientId The client ID of the Auth0 application starting the auth flow (e.g. mvUDKj8esZ2DFqLjEKzmta5rJEhXHbCP)
   * @param {string} options.connection The system of record for authorizing a user (e.g. Central-Connection for SS or google-oauth2 for Google SSO, etc)
   * @param {string} options.redirectUri The Auth0 callback URL (always SSAS /loginauth). This is where Auth0 redirects after auth and must be in Auth0's allowed callback list.
   * @param {string} [options.appLoginUrl] The consuming app's login page URL (for cancel/back/post-change-password navigation). Falls back to redirectUri if not provided.
   * @param {string} [options.redirectAuth0Uri] @deprecated Use redirectUri and appLoginUrl instead. Legacy parameter that conflated both concepts.
   * @param {string} options.targetUrl The original pathname of the unauthenticated request (passed to auth0 as 'targetUrl' and used to support deep linking in SS)
   * @param {string} options.appState JSON serialized values provided to Auth0 to persist in state through the auth session
   * @param {boolean} options.disableCaptcha This controls loading of the captcha widget only. It has absolutely no bearing on whether the backend expects to receive a captcha token
   * @param {string} [options.auth0LoginConfigToken] @deprecated SSAS now generates signed tokens server-side based on LD flags and environment. Client-provided tokens are only used for backwards compatibility during migration. This was a signed JWT containing { disableCaptcha: boolean }.
   * @param {boolean} options.enableSso Control whether or not the (shared) tenant login page should render the "signin with SSO" buttons
   * @param {boolean} options.enableRestoreAuth0LocalStorage Control whether or not the token values Auth0 has written to the environment fielding auth should be propagated and restored in the user's SSHome environment.
   * @param {string} [options.auth0WhiteLabelOptions] Allows for limited display control of the shared tenant login page
   * @param {string} [options.auth0WhiteLabelOptions.css] URL to a custom stylesheet applied to all auth pages (alias: white_label_css)
   * @param {string} [options.auth0WhiteLabelOptions.logo_url] URL to the brand logo (alias: white_label_logo_url)
   * @param {string} [options.auth0WhiteLabelOptions.icon_url] URL to the square brand icon used for spinner/favicon (alias: white_label_icon_url)
   * @param {string} [options.auth0WhiteLabelOptions.app_name] Brand name shown in UI text (alias: white_label_app_name)
   * @param {string} [options.auth0WhiteLabelOptions.marketing_button_url] Toolbar "Get Started" URL (alias: white_label_marketing_button_url)
   * @param {string} [options.auth0WhiteLabelOptions.marketing_button_display_text] Toolbar "Get Started" button text (alias: white_label_marketing_button_display_text)
   * @param {string} [options.auth0WhiteLabelOptions.app_login_support_email] Login support email shown in error messages (alias: white_label_app_login_support_email)
   * @param {string} [options.auth0WhiteLabelOptions.app_login_support_phone_number] Login support phone shown in error messages (alias: white_label_app_login_support_phone_number)
   * @param {string} [options.audience] optional audience to assign to the token. ss:webapi audience is used by default
   * @param {boolean} [options.shouldRenderRememberMe] If true render a "remember this device" checkbox
   * @param {'auth0'|'ssas'} [options.providerType] Control the auth provider used. Defaults to auth0 for backwards compat
   * @param {string} [options.postLoginHandoffUrl] Optional URL to hand off post-login handling to. When provided, the webhook handler will POST login data to this URL instead of using built-in handlers.
   * @param {Array<{storageType: string, accessTokenName: string, refreshTokenName?: string}>} [options.tokenStorageConfig] Custom token storage configuration. When provided, tokens will be stored according to this config instead of the default (_lat, _latv3 cookies).
   * @param {Array<{key: string, value: string, persistent?: boolean, seedOnly?: boolean, forAppIds?: string[]}>} [options.behavioralClaims] Optional anonymous key/value context data to pass through to the Auth0 actions pipeline. Each entry is added to the access token as a custom claim. When persistent=true the value is also written to the user's app_metadata and a schema entry is created so future logins can restore the claim even when no behavioralClaims are provided. When seedOnly=true the value is only written if the key is not already set in app_metadata — used for one-time initialization. forAppIds restricts schema restore to logins from the listed app IDs.
   * @param {boolean} [options.forceAuth] When true, forces a full loginWithRedirect regardless of existing session state. Use when the caller has already determined that a new authorize call is required (e.g. to write behavioral claims).
   * @example Loading and using the helper from an external app's HTML page
   * <script type="text/javascript" src="https://cdn.shipstation.com/auth0/shipstation-stage/auth0.login.min.js?cb=${cb}"></script>
   * <script type="text/javascript">
   *  const loginHelper = new auth0Helper.login.Auth0LoginHelper({ options });
   * </script>
   * @see {@link https://github.com/shipstation/shipstation-ui/blob/master/ss-ui-node/templates/auth0-login.ejs |Usage in v3-ui}
   */
  constructor(options) {
    this.ssOptions = options || {};
    this.audience = this.ssOptions.audience || Auth0Constants.AUTH0_DEFAULT_AUDIENCE;

    this.providerType = this.ssOptions.providerType || PROVIDER_AUTH0;
    this.providerAdapter = null;

    Auth0LocalStorageHelper.setSessionAudience(this.audience);
    Auth0LocalStorageHelper.setSessionClientId(this.ssOptions.clientId);

    Auth0UtilsBrowser.dumpVars();
    this._initLogger();
    window.features = {};

    // Resolve redirect URI and app login URL
    // Support both new (redirectUri/appLoginUrl) and legacy (redirectAuth0Uri) parameters
    const defaultRedirectUri = Auth0UtilsBrowser.locationWithNoSearchParams;

    // redirectUri: Auth0 callback URL (always SSAS /loginauth)
    // Priority: redirectUri > redirectAuth0Uri (legacy) > default
    this.ssOptions.redirectUri = this.ssOptions.redirectUri || this.ssOptions.redirectAuth0Uri || defaultRedirectUri;

    // appLoginUrl: The consuming app's login page (for cancel/back navigation)
    // Priority: appLoginUrl > redirectAuth0Uri (legacy, if different from redirectUri) > redirectUri
    this.ssOptions.appLoginUrl = this.ssOptions.appLoginUrl || this.ssOptions.redirectUri;

    // Maintain backwards compat: set redirectAuth0Uri to appLoginUrl for legacy code paths
    // TODO: Remove this once all usages are migrated to use redirectUri/appLoginUrl directly
    this.ssOptions.redirectAuth0Uri = this.ssOptions.appLoginUrl;

    // must be enabled if the fallback is disabled in v3 (it is right now)
    // if this is problematic, set it to false and enable `useRefreshTokensFallback` in v3 app
    this.ssOptions.enableRestoreAuth0LocalStorage = this.ssOptions.enableRestoreAuth0LocalStorage ?? false;

    this.domain = new Auth0Domain(this.ssOptions.domain);
    this.ssOptions.tenant = this.domain.resolveTenant();
    this.tenantUtils = new Auth0Tenant(this.ssOptions.tenant);
    this.socialIdpHelper = new Auth0SupportedIdps({
      tenantOrDomain: this.ssOptions.tenant,
      appLoginHandlerUrl: this.ssOptions.appLoginUrl,
    });
    this.ssOptions.firstLoginRedirectUrl = '';

    this.supportedSocialIdps = this.socialIdpHelper.supportedIdps;
    this.activeSocialIdps = this.socialIdpHelper.activeIdps;
    this.scriptLoader = new Auth0DynamicScriptLoader(this.ssOptions.tenant);

    this._whiteLabelOptions = this.ssOptions.auth0WhiteLabelOptions || {};
    this._tempSetAppEmailSupportAddress();
    this._buildCaptchaOptions();
    this._whiteLabelHelper = new Auth0WhiteLabelAssetHelper({
      tenantOrDomain: this.ssOptions.tenant,
      ...this._whiteLabelOptions,
    });
    this._appName = this._whiteLabelHelper.appName;
    this.audience = this.ssOptions.audience || Auth0Constants.AUTH0_DEFAULT_AUDIENCE;
    this.shouldRenderRememberMe = options.shouldRenderRememberMe;

    console.log(`new Auth0LoginHelper => ${JSON.stringify(this.ssOptions)}`);
  }

  _tempSetAppEmailSupportAddress = () => {
    console.log('Setting support email address...');
    // this data is to come from the implementing app, but since SE has not yet conformed to new white label standards, we will temporarily infer and set it here
    if (this._whiteLabelOptions.app_support_email) return;
    if (window.location.hostname.includes('shipengine')) {
      console.log('ShipEngine detected, setting support email to SE address...');
      this._whiteLabelOptions.app_support_email = 'support@shipengine.com';
    }
  };

  _buildCaptchaOptions = () => {
    this._captchaOptions = {
      disableCaptcha: this.ssOptions.disableCaptcha,
      captchaType: this.ssOptions.captchaType,
      auth0LoginConfigToken: this.ssOptions.auth0LoginConfigToken,
    };
  };

  _initLogger = () => {
    logger.initialize({
      sumoEndpoint: 'https://endpoint2.collection.us2.sumologic.com/receiver/v1/http/ZaVnC4dhaV1uAI96duDvnKsBH-5zBCQS4Z9yRC6qyLXrEdfezmbhON_4so37Qor_6qM1eto00rRWjhlaqRccMgcVRvrLAKRfcyNBdeziv7B_grZ3H8sjew==',
      sourceName: 'Auth0LoginHelper',
    });
  };

  _handledUnexpectedException = (e) => {
    logger.error(`Unexpected error in Auth0LoginHelper => ${Auth0UtilsBrowser.errorToString(e)}`);
    if (this.sentry) {
      this.sentry.captureException(e);
    }
  };

  get _devIsLocalCdnScript() {
    const resources = performance.getEntriesByType('resource');
    const isLocal = resources.some(r => r.name.includes('auth0.login.min.js') && r.name.includes('localcdn.'));
    console.log('login-helper: isLocalCdnScript =>', resources);
    console.log(`login-helper: isLocalCdnScript => ${isLocal}`);
    return isLocal;
  }

  /**
   * @param {string} [overrideConnection] - optional connection override
   * returns the auth provider adapter. Also sets this.providerAdapter to the adapter instance
   */
  _createClient = async (overrideConnection = null) => {
    console.log(`Creating auth client for provider type: ${this.providerType}...`);

    const a0ClientOptions = {
      domain: this.ssOptions.domain,
      clientId: this.ssOptions.clientId,
      authorizationParams: {
        audience: this.audience,
        redirect_uri: this.ssOptions.redirectUri,
        connection: overrideConnection || this.ssOptions.connection,
        scope: 'openid profile email offline_access',
        disableCaptcha: this.ssOptions.disableCaptcha,
        captchaType: this.ssOptions.captchaType,
        enableSso: this.ssOptions.enableSso,
        auth0LoginConfigToken: this.ssOptions.auth0LoginConfigToken,
        devIsLocalCdn: this.tenantUtils.isLocalEnv ? this._devIsLocalCdnScript : null,
        shouldRenderRememberMe: this.shouldRenderRememberMe,
        ...this.ssOptions.auth0WhiteLabelOptions,
      },
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
    };

    // dev: support localhost multi-partition testing
    if (this.tenantUtils.isLocalEnv && window.location.hostname.includes(Auth0Constants.AUTH0_DEV_LOCAL_MULTI_PARTITION_TESTING_KEY)) {
      console.log(`${Auth0Constants.AUTH0_DEV_LOCAL_MULTI_PARTITION_TESTING_KEY} detected, setting dev_amfa_redirect_subdomain to local "other" partition`);
      a0ClientOptions.authorizationParams.dev_amfa_redirect_subdomain = `local${Auth0Constants.AUTH0_DEV_LOCAL_MULTI_PARTITION_TESTING_KEY}`;
    }

    const workstationAuthTokens = Auth0UtilsBrowser.getAllWorkstationAuthorizationTokens(true);
    if (workstationAuthTokens) {
      a0ClientOptions.authorizationParams.workstationAuthTokens = JSON.stringify(workstationAuthTokens);
    }
    if (this.ssOptions.behavioralClaims && this.ssOptions.behavioralClaims.length > 0) {
      a0ClientOptions.authorizationParams.behavioralClaims = JSON.stringify(this.ssOptions.behavioralClaims);
    }
    if (this.ssOptions.auth0WhiteLabelOptions) {
      a0ClientOptions.authorizationParams.whiteLabelJson = JSON.stringify(this.ssOptions.auth0WhiteLabelOptions);
    }

    // Pass the app's login URL to Auth0 actions so they can include it in redirect flows
    // This allows cancel/back navigation in force change password and MFA flows to return to the original app
    // Use appLoginUrl (the consuming app's login page), not redirectUri (the Auth0 callback URL)
    if (this.ssOptions.appLoginUrl) {
      a0ClientOptions.authorizationParams.appLoginUrl = this.ssOptions.appLoginUrl;
    }

    // any client app may choose to specify its own "remember this device" device ID. It is optional though
    // If there is one provided, write it to a cookie so it snaps in to the existing flow
    const clientProvidedDeviceId = this._qs.get(Auth0Constants.AUTH0_CLIENT_REMEMBER_ME_DEVICE_ID_OVERRIDE_KEY);
    if (clientProvidedDeviceId) {
      console.log(`Client provided device ID found: ${clientProvidedDeviceId}`);
      // the write it to a cookie so it snaps in to the existing flow
      Auth0CookieHelper.setCookie(
        Auth0Constants.AUTH0_REMEMBER_ME_DEVICE_ID_KEY,
        clientProvidedDeviceId,
        { expiry: Auth0CookieHelper.getExpirationFromDays(Auth0Constants.AUTH0_REMEMBER_ME_NUM_DAYS) },
      );
    }

    // a device ID can be sourced from one of 3 places (in this order):
    // 1.) a client may provide it when starting auth in the qs; 2.) a cookie; 3.) if neither of those are present, we generate a new one (at time of login)
    const rememberDevice = Auth0CookieHelper.getCookieValue(Auth0Constants.AUTH0_REMEMBER_ME_DEVICE_ID_KEY);
    if (rememberDevice) {
      a0ClientOptions.authorizationParams.should_remember_device = true; // default value... the login page UI can override if necessary
      a0ClientOptions.authorizationParams.remember_device_id = rememberDevice;
      // if a cookie was found update the cookie's expiration
      Auth0CookieHelper.setCookie(
        Auth0Constants.AUTH0_REMEMBER_ME_DEVICE_ID_KEY,
        rememberDevice,
        { expiry: Auth0CookieHelper.getExpirationFromDays(Auth0Constants.AUTH0_REMEMBER_ME_NUM_DAYS) },
      );
    }

    // check for an auto login request for a newly registered user, if found pass it to the login page
    const onboardParam = this._qs.get('onboarding');
    if (onboardParam) {
      const onboardParamUsername = this._qs.get('username');
      const onboardParamNonceOrJwt = this._qs.get('nonce'); // todo: get marketing to update this name
      console.log(`onboard param found: ${onboardParam} for username: ${onboardParamUsername} and nonce: ${onboardParamNonceOrJwt}`);
      a0ClientOptions.authorizationParams.onboardUsername = onboardParamUsername;
      a0ClientOptions.authorizationParams.onboardNonceOrJwt = onboardParamNonceOrJwt;
    }

    // Allow bypassing progressive auth via URL param (for automation/synthetic monitoring)
    const progressiveAuthParam = this._qs.get('progressiveAuth');
    if (progressiveAuthParam === 'false') {
      console.log('Progressive auth bypass detected via URL param');
      a0ClientOptions.authorizationParams.progressiveAuth = 'false';
    }

    console.log(`Auth0 Client options created with: ${JSON.stringify(a0ClientOptions)}`);

    if (this.providerType === PROVIDER_SSAS) {
      const adapter = new ShipStationOIDCProviderAdapter({
        issuerUrl: this.ssOptions.ssasIssuerUrl,
        clientId: this.ssOptions.clientId,
        redirectUri: this.ssOptions.redirectUri,
        scope: 'openid profile email offline_access',
      });
      await adapter.createClient();
      this.providerAdapter = adapter;

      return adapter;
    }

    const adapter = new Auth0ProviderAdapter({
      domain: this.ssOptions.domain,
      clientId: this.ssOptions.clientId,
      tenant: this.ssOptions.tenant,
      authServiceBaseUrl: resolveSsasBaseUrl('auth-stage', 'shipstation.com,sslocal.com', window.location.hostname),
      audience: this.audience,
      connection: overrideConnection || this.ssOptions.connection,
    });
    await adapter.createClient(a0ClientOptions);
    this.providerAdapter = adapter;

    return adapter;
  };

  _isCodeExchange = () => {
    const { search } = window.location;
    return search.includes('code=') && search.includes('state=');
  };


  /**
   * Cleans the browser history so the 'back' button doesn't take the user to an interstitial auth page
   * @param {*} appState
   */
  _onRedirectCallback = (appState) => {
    const { hash } = window.location;
    let newHistory = appState && appState.redirectOverride ? appState.redirectOverride : appState.targetUrl;
    newHistory = newHistory || window.location.pathname;
    window.history.replaceState({}, document.title, newHistory);

    // eslint-disable-line no-self-assign
    if (hash) {
      window.location.hash = hash;
    }
  };

  _buildForceTpRedirect = (url = window.location.href) => {
    const splitter = '#';
    const urlParts = url.split(splitter);
    if (urlParts.length !== 2) {
      return window.location.href;
    }
    const newUrlParts = [];
    // eslint-disable-next-line
    newUrlParts[0] = urlParts[0];
    newUrlParts[1] = `${urlParts[0].indexOf('?') > 0 ? '&' : '?'}forcetp=true`;
    newUrlParts[2] = `${splitter}${urlParts[1]}`;

    return newUrlParts.join('');
  };

  _setAuth0Authenticated = (authenticated) => {
    Auth0CookieHelper.setCookie(Auth0Constants.AUTH0_IS_AUTHENTICATED, authenticated, { expiry: Auth0CookieHelper.getExpirationFromDays(1) });
  };

  _setIsNativeSsoSeller = (ssoIdpName) => {
    Auth0CookieHelper.setCookie(Auth0Constants.AUTH0_IS_NATIVE_SSO_SELLER, ssoIdpName, { expiry: Auth0CookieHelper.getExpirationFromDays(365) });
  };

  _removeIsNativeSsoSeller = () => {
    Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_IS_NATIVE_SSO_SELLER);
  };

  _setIsLogin = (isLogin) => {
    Auth0CookieHelper.setCookie(Auth0Constants.AUTH0_IS_LOGIN_PROVIDER, isLogin, { expiry: Auth0CookieHelper.getExpirationFromDays(1) });
  };

  _showErrorOnLoginPage = (errMsg) => {
    Auth0UtilsBrowser.setAuth0IsError(errMsg);
    Auth0StateHelper.resetAuthState();
    window.location = '/';
  };

  _setAndDisplayErrorOnLoginPage = async (error) => {
    const errMsg = Auth0Localizer.resourceKeyFromMessage(error);
    await this._startLogout({
      connectionRefreshContext: this._idpContext?.id || this.auth0ConnectionRefreshIdpContext?.idpId,
      errMsg,
    });
  };

  _setAndDisplayErrorOnNotificationPage = async (error, replacementVars = null) => {
    const errMsg = Auth0Localizer.resourceKeyFromMessage(error);
    replacementVars = replacementVars || error.keyReplacementVars;
    await new Auth0NotificationPageHelper({
      name: 'LoginHelper>NotificationPage',
      notificationText: errMsg,
      notificationTextReplacementVars: replacementVars,
      showNotificationFooter: error.showNotificationFooter === undefined ? true : error.showNotificationFooter,
      // optionals
      // notificationTitle: 'toolbar-action-new-to-app',
      // notificationTitleReplacementVars: { appName: 'asdf' },
      // notificationFooter: 'error-create-user-code',
      // notificationFooterReplacementVars: { code: 'asdf' },
      notificationType: Auth0NotificationPageHelper.NOTIFICATION_TYPE_ERROR,
      tenantOrDomain: this.ssOptions.tenant,
      whiteLabelOptions: this._whiteLabelOptions,
    }).render();
  };

  _isRequestForTpPublicAsset = () => {
    return (
      window.location.hash && window.location.hash.indexOf('#/public') === 0
    );
  };

  _exchangeCode = () => {
    return this.providerAdapter
      .handleRedirectCallback()
      .then((appData) => {
        this._onRedirectCallback(appData);
        return appData.appState;
      })
      .then((appState) => {
        return this.providerAdapter.isAuthenticated().then(isAuthenticated => ({ isAuthenticated, appState }));
        // throw new Error('forced error');
      })
      .catch((e) => {
        this._handledUnexpectedException(e);
        window.history.replaceState({}, document.title, '/');
        throw e;
      });
  };

  _overrideRedirectUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams && urlParams.get('redirect_uri')) {
      return urlParams.get('redirect_uri');
    }

    if (this.redirectContextIdp) {
      return this.redirectContextIdp?.redirectUrl;
    }

    if (this.auth0ConnectionRefreshIdpContext?.redirectUrl) {
      // this is the "return to" page after the IdP tokens are "swapped" for SS tokens (refresh request)
      return this.auth0ConnectionRefreshIdpContext?.redirectUrl;
    }
  };

  /**
   * Iterative approach to allow consuming apps to supply their own appState and appState.targetUrl
   * @returns {string} URL for redirect
   * @todo Extend the constructor and all appState related method to take in an appState object.
   */
  _getTargetUrl = () => {
    const clientSuppliedTargetUrl = (
      this.ssOptions.appState?.targetUrl // undefined until @todo is done
      || this.ssOptions.targetUrl
    );
    console.log(`auth0-helper: _getTargetUrl: clientSuppliedTargetUrl => ${clientSuppliedTargetUrl}`);
    console.log('auth0-helper: _getTargetUrl: unwrap-check-v2 - pathname will be checked for /loginauth');

    if (!clientSuppliedTargetUrl) {
      return undefined;
    }

    try {
      const baseUrl = window.location.origin;
      let targetUrl = new URL(clientSuppliedTargetUrl, baseUrl);

      // If the resolved URL is a /loginauth path that itself contains a targetUrl param,
      // unwrap the nested targetUrl. This handles the case where ss-ui-node master sets
      // ssOptions.targetUrl to req.originalUrl (/loginauth?...&targetUrl=https://...).
      const nestedTargetUrl = targetUrl.searchParams.get('targetUrl');
      if (nestedTargetUrl && (targetUrl.pathname === '/loginauth' || targetUrl.pathname.endsWith('/loginauth'))) {
        console.log(`auth0-helper: _getTargetUrl: unwrapping nested targetUrl from loginauth path => ${nestedTargetUrl}`);
        targetUrl = new URL(nestedTargetUrl, baseUrl);
      }

      if (targetUrl.searchParams.get('onboarding') === 'true') {
        // the auto-login url is an invalid targetUrl to redirect to after auth
        // return nothing and the partition root will be used instead
        console.log('auth0-helper: _getTargetUrl: onboard auto-login URL detected, ignoring targetUrl...');
        return undefined;
      }

      return targetUrl;
    } catch (err) {
      console.error(`auth0-helper: _getTargetUrl: err => ${err.message}`);
      return undefined;
    }
  };

  /**
   * @deprecated
   * Default value that was shipped in 2.6.0
   * @returns {string} window.location.origin
   */
  _getDefaultTargetUrl = () => window.location.origin;


  /**
   * The appState object we send to the auth provider that will be stored in state for the duration of the auth session.
   * @typedef {Object} AppState
   * @property {string} targetUrl
   * @property {string} redirectOverride
   * @property {boolean} shouldUseZendeskAuth
   * @property {string} [additionalState]
   */

  /**
   *
   * @returns {AppState} targetUrl:
      // The app URL that user is attempting to access, it is derived from the current window.location at time of auth flow start
      // redirectOverride:
      // Ignores the current window.location and forces redirect to specified resource (e.g. help center)
   */
  _createAppState = () => {
    console.log('createAppState: ssOptions =>', JSON.stringify(this.ssOptions));
    console.log('createAppState: ssOptions.targetUrl =>', this.ssOptions?.targetUrl);
    console.log('createAppState: ssOptions.appLoginUrl =>', this.ssOptions?.appLoginUrl);
    console.log('createAppState: _getTargetUrl() =>', this._getTargetUrl()?.toString());
    console.log('createAppState: _getDefaultTargetUrl() =>', this._getDefaultTargetUrl());
    const redirectOverride = this._overrideRedirectUrl();
    // redirecting to the help center requires a call to v2 node proxy to build a JWT and redirect to zendesk auth.
    const shouldUseZendeskAuth = redirectOverride && redirectOverride.includes('help.shipstation');

    const resolvedTargetUrl = this._getTargetUrl() || this._getDefaultTargetUrl();
    console.log('createAppState: resolvedTargetUrl =>', resolvedTargetUrl?.toString());

    const appState = {
      targetUrl: resolvedTargetUrl,
      redirectOverride,
      shouldUseZendeskAuth,
    };

    // sometimes we'll need additional state preserved like iovation blackbox tokens etc
    // additionalState must always be a serialized JSON string
    if (this._qs.get(Auth0Constants.AUTH0_ADDITIONAL_APP_STATE)) {
      console.log('Detected additional state, adding...');
      appState[Auth0Constants.AUTH0_ADDITIONAL_APP_STATE] = this._qs.get(Auth0Constants.AUTH0_ADDITIONAL_APP_STATE);
    } else if (this.redirectContextIdp && Auth0UtilsBrowser.getOption({ cookieName: Auth0Constants.AUTH0_LINKING_REQUEST_STATE }, true)) {
      // When the IDP login was triggered from a linking context (e.g. v3 profile page via cookie),
      // inject userInitiatedLinking so the post-login handler recognizes the linking flow.
      // Button-click flows set this via signinWithRedirectHandler's URL param; cookie-driven flows
      // do not have the URL param so we inject it here.
      console.log('Detected IDP linking context from cookie, injecting userInitiatedLinking into additionalState');
      appState[Auth0Constants.AUTH0_ADDITIONAL_APP_STATE] = JSON.stringify({ userInitiatedLinking: true });
    }

    // Preserve post-login handler configuration in appState so it survives the Auth0 redirect
    // These values come from the calling app via Auth0LoginHelper options and need to be available
    // after the PKCE code exchange when _runPostLoginAction is called
    if (this.ssOptions.postLoginHandoffUrl) {
      appState.postLoginHandoffUrl = this.ssOptions.postLoginHandoffUrl;
      console.log(`createAppState: preserving postLoginHandoffUrl => ${this.ssOptions.postLoginHandoffUrl}`);
    }
    if (this.ssOptions.tokenStorageConfig) {
      appState.tokenStorageConfig = this.ssOptions.tokenStorageConfig;
      console.log(`createAppState: preserving tokenStorageConfig => ${JSON.stringify(this.ssOptions.tokenStorageConfig)}`);
    }

    return appState;
  };

  _loginWithRedirect = async () => {
    const urlHash = window.location.hash;

    // Thread authOptionsToken into redirectUri so SSAS can re-inject WL config on the callback
    // page before the spinner renders. The token was embedded into redirectUri by auth0-login.ejs
    // (from the server-minted token), so it's already present in this.ssOptions.redirectUri.
    // window._ssasAuthOptionsToken is the fallback for any code path where redirectUri was built without it.
    const redirectUri = (() => {
      const base = this.ssOptions.redirectUri || `${window.location.origin}/loginauth${urlHash}`;
      const token = window._ssasAuthOptionsToken;
      if (!token) return base;
      try {
        const url = new URL(base);
        if (!url.searchParams.has('authOptionsToken')) {
          url.searchParams.set('authOptionsToken', token);
        }
        return url.toString();
      } catch {
        return base;
      }
    })();

    const loginOptions = {
      authorizationParams: {
        // Use redirectUri (Auth0 callback URL, always SSAS /loginauth) for the OAuth redirect
        redirect_uri: redirectUri,
      },
      appState: this._createAppState(),
    };


    const errMsg = this._extractLoginPageErrorAtStartMessage() || Auth0CookieHelper.getCookieValue(Auth0Constants.AUTH0_IS_IN_ERROR_STATE) || Auth0CookieHelper.getCookieValue(Auth0Constants.LOGOUT_MESSAGE);
    if (errMsg) {
      loginOptions.authorizationParams.error_msg = errMsg;
      Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_IS_IN_ERROR_STATE);
      Auth0CookieHelper.deleteCookie(Auth0Constants.LOGOUT_MESSAGE);
    }

    const infoMsg = this._extractLoginPageInfoMessageAtStartMessage();
    if (infoMsg) {
      loginOptions.authorizationParams.info_msg = infoMsg;
    }

    this._setIsLogin(!urlHash || urlHash.match(/#\/?$/i));

    if (this._idpContext) {
      try {
        console.log('_idpContext exists, call beforeAuthRedirect()');
        // only supported by auth0 for now so just pass the underlying client
        this._idpContext.beforeAuthRedirect(loginOptions, this.providerAdapter.getUnderlyingClient());
      } catch (error) {
        this._handledUnexpectedException(error);
        logger.error(`IdP "${this._idpContext.id}" failed beforeAuthRedirect actions, aborting idp login => ${error.message}`);
        this._idpContext.reset();
        window.location = '/';
      }
    }

    if (this._qs.get('reset') || errMsg) {
      console.warn('error or reset state => force loginWithRedirect to re-authenticate');
      loginOptions.authorizationParams.prompt = 'login'; // forces login action
    }

    console.log(`auth0-helper: _loginWithRedirect => ${JSON.stringify(loginOptions)} `);
    this.providerAdapter.loginWithRedirect(loginOptions);
  };

  // just a dev helper function so that the entire link / unlink flow can be tested without leaving this repo
  _devUnlinkAllSsoAccounts = async () => {
    await this.providerAdapter.devUnlinkAllSsoAccounts();
  };

  _getIdPObjectForCurrentUserProfile = (userData) => {
    try {
      return this.socialIdpHelper.getIdpFromUserProfile(userData.user);
    } catch (error) {
      this._handledUnexpectedException(error);
      logger.warn(`Unable to find IDP object for user profile => ${JSON.stringify(userData)}`);
    }
  };

  _isNativeSsoSeller = (userData) => {
    const ssoSource = userData?.app_metadata?.native_sso_seller?.toLowerCase();
    if (!ssoSource) return false;
    return ssoSource !== 'false';
  };

  _checkAndEmitNativeSsoSellerMarker = (userData) => {
    const nativeSsoProvider = this._isNativeSsoSeller(userData);
    if (nativeSsoProvider) {
      console.log(`user identified as native SSO via ${nativeSsoProvider}...`);
      this._setIsNativeSsoSeller(nativeSsoProvider);
    } else {
      this._removeIsNativeSsoSeller();
    }
  };

  _devPreRedirectInit = async () => {
    // various items to make dev easier... auto unlink accounts, bypass beta opt ins, etc
    // will not run in prod or stage but will be bundled in prod so nothing that bypasses security should ever appear in here... helpers only
    if (Auth0UtilsBrowser.getOption({ cookieName: Auth0Constants.AUTH0_UNLINK_ALL })) {
      console.log('AUTH0_UNLINK_ALL DETECTED...');
      await this._devUnlinkAllSsoAccounts();
    }
  };

  _isRefreshLoginProfileRequest = () => {
    if (Auth0UtilsBrowser.hasProp(this, 'auth0ConnectionRefreshIdpContext')) {
      return this.auth0ConnectionRefreshIdpContext;
    }

    this.auth0ConnectionRefreshIdpContext = Auth0UtilsBrowser.getOption({
      keyName: Auth0Constants.AUTH0_CHANNEL_REFRESH,
    }, true, true);

    if (this.auth0ConnectionRefreshIdpContext) {
      console.log(`auth channel refresh detected for ${JSON.stringify(this.auth0ConnectionRefreshIdpContext)}`);
      return this.auth0ConnectionRefreshIdpContext;
    }
    return false;
  };

  _isIdpSigninRequest = () => {
    if (Auth0UtilsBrowser.hasProp(this, 'redirectContextIdpId')) {
      return this.redirectContextIdp;
    }

    // TRACE: log all cookies to diagnose linking flow cookie visibility
    console.log(`_isIdpSigninRequest: document.cookie keys present: [${document.cookie.split(';').map(c => c.trim().split('=')[0]).join(', ')}]`);

    this.redirectContextIdp = Auth0UtilsBrowser.getOption({
      searchParamName: 'login_redirect',
      cookieName: Auth0Constants.AUTH0_IDP_REDIRECT_LOGIN,
    }, true, true);

    console.log(`_isIdpSigninRequest: AUTH0_IDP_REDIRECT_LOGIN cookie name = "${Auth0Constants.AUTH0_IDP_REDIRECT_LOGIN}", resolved value = ${JSON.stringify(this.redirectContextIdp)}`);

    if (this.redirectContextIdp) {
      console.log(`login with redirect for idp detected for ${JSON.stringify(this.redirectContextIdp)}`);
      return this.redirectContextIdp;
    }
    return false;
  };

  _isIpdInitiatedSigninRequest = () => {
    if (Auth0UtilsBrowser.hasProp(this, 'idpInitiatedSigninContext')) {
      return this.idpInitiatedSigninContext;
    }

    this.idpInitiatedSigninContext = Auth0UtilsBrowser.getOption({
      searchParamName: Auth0Constants.AUTH0_IDP_INITIATED_KEY,
      cookieName: Auth0Constants.AUTH0_IDP_INITIATED_KEY,
    }, true);

    if (this.idpInitiatedSigninContext) {
      console.log(`idp initiated login detected for ${this.idpInitiatedSigninContext}`);
      return this.idpInitiatedSigninContext;
    }
    return false;
  };

  _isLogoutRequest = () => {
    const forceLogoutFromCookie = Auth0UtilsBrowser.getOption({
      cookieName: Auth0Constants.AUTH0_FORCE_LOGOUT,
    }, true, true);

    if (forceLogoutFromCookie) {
      console.log(`force logout from cookie detected => ${JSON.stringify(forceLogoutFromCookie)}`);
      return forceLogoutFromCookie;
    }

    const forceLogoutSearchParam = Auth0UtilsBrowser.getOption({
      searchParamName: 'force_logout',
    });

    if (forceLogoutSearchParam) {
      console.log('force logout from qs detected, fabricate logout instructions object...');
      const derivedType = forceLogoutFromCookie === true ? Auth0Constants.LOGOUT_TYPE_REDIRECT : forceLogoutFromCookie;
      return { logoutType: derivedType };
    }
    return false;
  };

  _isDevUnlinkAllRequest = () => {
    const unlinkAll = Auth0UtilsBrowser.getOption({
      searchParamName: 'dev_unlink_all',
      cookieName: Auth0Constants.AUTH0_UNLINK_ALL,
    });

    if (unlinkAll) {
      console.log('DEV - unlink all SSO idps detected');
      return unlinkAll;
    }
    return false;
  };

  _hasLoginPageErrorAtStart = () => {
    return (this._qs.get(Auth0Constants.AUTH_ERROR_KEY1) || this._qs.get(Auth0Constants.AUTH_ERROR_KEY2));
  };

  _extractLoginPageErrorAtStartMessage = () => {
    if (!this._hasLoginPageErrorAtStart()) {
      console.log('NO ERROR QS FOUND!');
      return;
    }
    const msg = this._qs.get(Auth0Constants.AUTH_ERROR_KEY2) || this._qs.get(Auth0Constants.AUTH_ERROR_KEY1);
    if (!msg) return;

    console.log('external login page error detected....');
    return decodeURIComponent(msg);
  };

  _hasNotificationPageErrorAtStart = () => {
    return (this._qs.get(Auth0Constants.AUTH_ERROR_KEY1) || this._qs.get(Auth0Constants.AUTH_ERROR_KEY2))
      && this._qs.get(Auth0Constants.AUTH_ERROR_DISPLAY_ON_KEY) === Auth0Constants.AUTH_ERROR_DISPLAY_ON_NOTIFICATION_PAGE_VAL;
  };

  _extractNotificationPageErrorAtStartMessage = () => {
    if (!this._hasNotificationPageErrorAtStart()) return;
    // Prefer error_description (detailed) over error (short code) — the description
    // contains the actionable message (e.g. "Callback URL mismatch. <url> is not in the list...")
    const msg = this._qs.get(Auth0Constants.AUTH_ERROR_KEY2) || this._qs.get(Auth0Constants.AUTH_ERROR_KEY1);
    if (!msg) return;

    console.log('external notification page error detected....');
    return decodeURIComponent(msg);
  };

  _hasLoginPageInfoMessageAtStart = () => {
    return this._qs.get(Auth0Constants.AUTH_INFO_MESSAGE);
  };

  _extractLoginPageInfoMessageAtStartMessage = () => {
    if (!this._hasLoginPageInfoMessageAtStart()) {
      console.log('NO INFO QS FOUND!');
      return;
    }
    const msg = this._qs.get(Auth0Constants.AUTH_INFO_MESSAGE);
    if (!msg) return;

    console.log('external login page info message detected....');
    return decodeURIComponent(msg);
  };

  _startLogout = async (options = {}) => {
    // connectionRefreshContext = null, errMsg = null
    options.logoutType = options.logoutType ?? Auth0Constants.LOGOUT_TYPE_REDIRECT;
    console.log(`start logout from ${window.location} with options => ${JSON.stringify(options)}...`);
    return this._createClient(options.connectionRefreshContext)
      .then(() => {
        console.log('auth0 client created for logout to origin...');
        this.providerAdapter.cleanAuthState();

        // Clear Auth0 SPA JS localStorage cache to prevent re-authentication
        // when returning to the login page
        Auth0LocalStorageHelper.resetAuth0State();

        // Clear the force logout cookie since we've handled it
        Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_FORCE_LOGOUT);

        if (options.logoutType === Auth0Constants.LOGOUT_TYPE_NO_REDIRECT) {
          return this._logoutWithoutRedirect();
        }
        if (options.logoutType === Auth0Constants.LOGOUT_TYPE_FEDERATED_NO_REDIRECT) {
          return this._logoutWithoutRedirect(true);
        }
        if (options.logoutType === Auth0Constants.LOGOUT_TYPE_FEDERATED_REDIRECT) {
          return this._logoutToAppLoginPage(options.errMsg, true);
        }

        return this._logoutToAppLoginPage(options.errMsg, false, options.shouldPreserveQueryParams);
      });
  };

  _logoutToAppLoginPage = (errMsg, federated = false, shouldPreserveQueryParams = false) => {
    // Use appLoginUrl (the consuming app's login page) for logout redirects
    let redirectUrl = errMsg ? `${window.location.origin}?error=${encodeURIComponent(errMsg)}` : this.ssOptions.appLoginUrl;
    console.log('Auth0LoginHelper => logout and redirect to app login url', redirectUrl);
    if (federated) {
      console.log('Auth0LoginHelper => federated logout (will also attempt to logout of any social IdPs)');
    }
    if (shouldPreserveQueryParams) {
      const originalSearchParams = window.location.search;
      if (originalSearchParams) {
        const url = new URL(redirectUrl);
        const originalParams = Auth0UtilsBrowser.parseQueryString(originalSearchParams);

        // Preserve original query parameters
        originalParams.forEach((value, key) => {
          url.searchParams.set(key, value);
        });

        redirectUrl = url.toString();
      }
    }

    return this.providerAdapter.logout({
      logoutParams: {
        returnTo: redirectUrl,
        federated,
      },
    });
  };

  _logoutWithoutRedirect = (federated = false) => {
    console.log('Auth0LoginHelper => logout without redirect');
    if (federated) {
      console.log('Auth0LoginHelper => federated logout (will also attempt to logout of any social IdPs)');
    }
    return this.providerAdapter.logout({
      openUrl: false,
      logoutParams: {
        federated,
      },
    });
  };

  _setIdpContext = () => {
    if (this._isIdpSigninRequest()) {
      // then we are authenticating with SSO IdP
      this._idpContext = this.socialIdpHelper.getIdpById(this.redirectContextIdp?.idpId);
      console.log(`IDP CONTEXT: AUTH DETECTED FOR ${this._idpContext.name}`);
    } else if (this._isIpdInitiatedSigninRequest()) {
      this._idpContext = this.socialIdpHelper.getIdpById(this.idpInitiatedSigninContext);
      console.log(`IDP CONTEXT: IDP INITIATED AUTH DETECTED FOR ${this._idpContext.name}`);
    }
  };

  _runPreLoginActions = async () => {
    const preLoginFlowHandlers = new Auth0PreLoginHandlerHelper({
      ssOptions: this.ssOptions,
      tenantOrDomain: this.ssOptions.tenant,
      clientId: this.ssOptions.clientId,
      whiteLabelOptions: this._whiteLabelOptions,
      captchaOptions: this._captchaOptions,
      idpContext: this._idpContext,
    });
    const handled = await preLoginFlowHandlers.selectAndRunPostLoginActions();
    return handled;
  };

  _runPostLoginAction = async (data) => {
    try {
      // Extract post-login handler config from appState (preserved through Auth0 redirect)
      // These values are stored in appState by _createAppState() before loginWithRedirect
      // and are available here after the PKCE code exchange
      const { appState } = data;
      const postLoginHandoffUrl = appState?.postLoginHandoffUrl ?? this.ssOptions.postLoginHandoffUrl;
      const tokenStorageConfig = appState?.tokenStorageConfig ?? this.ssOptions.tokenStorageConfig;

      if (postLoginHandoffUrl) {
        console.log(`_runPostLoginAction: using postLoginHandoffUrl from ${appState?.postLoginHandoffUrl ? 'appState' : 'ssOptions'}`);
      }
      if (tokenStorageConfig) {
        console.log(`_runPostLoginAction: using tokenStorageConfig from ${appState?.tokenStorageConfig ? 'appState' : 'ssOptions'}: ${JSON.stringify(tokenStorageConfig)}`);
      } else {
        console.warn('_runPostLoginAction: tokenStorageConfig not found in appState or ssOptions — tokens will use default storage (_lat/_latv3)');
      }

      this._postLoginHelper = new Auth0PostLoginHandlerHelper({
        ssOptions: this.ssOptions,
        tenantOrDomain: this.ssOptions.tenant,
        clientId: this.ssOptions.clientId,
        audience: this.audience,
        whiteLabelOptions: this._whiteLabelOptions,
        captchaOptions: this._captchaOptions,
        idpContext: this._idpContext,
        providerType: this.providerType,
        postLoginHandoffUrl,
        tokenStorageConfig,
      });
      await this._postLoginHelper.selectAndRunPostLoginActions(data);
    } catch (error) {
      logger.error(`Auth0LoginHelper._runPostLoginAction => redirect handler failed "${JSON.stringify(error)}"`, error);
      this._handledUnexpectedException(error);
      if (error.displayOnNotificationPage) {
        error.showNotificationFooter = !(error.hideContactSupportMessage || false);
        this._setAndDisplayErrorOnNotificationPage(error);
      } else {
        this._setAndDisplayErrorOnLoginPage(error);
      }
    }
  };


  _startLogin = (auth0ClientContext = null) => {
    console.log(`start login from ${window.location} with connection override => ${auth0ClientContext || 'none'}...`);

    this._createClient(auth0ClientContext)
      .then(() => {
        console.log('auth0 client created...');
        if (this._isCodeExchange()) {
          console.log('auth0 code exchange...');
          return this._exchangeCode();
        }

        const appState = {};
        const redirectOverride = this._overrideRedirectUrl();

        if (redirectOverride) {
          appState.redirectOverride = redirectOverride;
        }

        // forceAuth: caller has already determined a new authorize call is required
        // (e.g. to ensure behavioral claims reach Auth0 actions).
        if (this.ssOptions.forceAuth) {
          console.log('_startLogin: forceAuth=true — forcing loginWithRedirect');
          this._loginWithRedirect();
          return;
        }

        return this.providerAdapter.isAuthenticated().then(isAuthenticated => ({ isAuthenticated, appState }));
      })
      .then(async (authData) => {
        if (!authData) return; // loginWithRedirect was called — nothing to do
        console.log(`isAuth: ${JSON.stringify(authData)}`);
        const { isAuthenticated } = authData || {};
        let { appState } = authData || {};
        // When auth0-spa-js returns isAuthenticated=true from its internal session check
        // (silent auth iframe), appState comes back empty — the targetUrl and tokenStorageConfig
        // were never stored in the auth0-spa-js cache. Reconstruct from ssOptions so post-login
        // handlers have the correct redirect target and storage config.
        // Only inject targetUrl when it is an absolute URL — relative paths (e.g. '/') cause
        // sshome's _getValidTargetUrl to throw. When targetUrl is relative, the sshome fallback
        // to window.location.origin is already correct.
        if (isAuthenticated && (!appState || Object.keys(appState).length === 0)) {
          // Use _getTargetUrl() rather than ssOptions.targetUrl directly — it handles unwrapping
          // nested targetUrl params (e.g. /loginauth?...&targetUrl=https://...) that ss-ui-node
          // sets when it derives targetUrl from req.originalUrl.
          const resolvedTargetUrl = this._getTargetUrl()?.href;
          appState = {
            ...(resolvedTargetUrl && { targetUrl: resolvedTargetUrl }),
            tokenStorageConfig: this.ssOptions.tokenStorageConfig,
            postLoginHandoffUrl: this.ssOptions.postLoginHandoffUrl,
          };
          console.log(`auth0-helper: appState reconstruction: resolvedTargetUrl=${resolvedTargetUrl}`);
        }
        console.log(`_startLogin auth check: isAuthenticated=${isAuthenticated}, redirectContextIdp=${JSON.stringify(this.redirectContextIdp)}`);
        if (isAuthenticated && !this.redirectContextIdp) {
          this._setAuth0Authenticated('true');
          console.log('good auth, get user from auth0, force refresh access token...');
          const user = await this.providerAdapter.getUser();
          this._checkAndEmitNativeSsoSellerMarker(user);
          return { user, appState, isAuthenticated };
        }
        console.log('no auth detected, call _loginWithRedirect...');
        this._loginWithRedirect();
      })
      .then(async (data) => {
        if (!data || !data.user || !data.isAuthenticated) return;

        console.log(`IdP user dump: ${JSON.stringify(data)}`);

        let loginIdp;
        if (this.providerType === PROVIDER_AUTH0) {
          // This is only supported by auth0 for now, so just pass the auth0 client to avoid
          // more changes
          loginIdp = this._getIdPObjectForCurrentUserProfile(data);
          loginIdp?.afterAuthRedirect(data, this.providerAdapter.getUnderlyingClient());
        }

        if (this.tenantUtils.isTenantForDev) {
          console.log('Running dev-only pre-redirect init...');
          await this._devPreRedirectInit();
        }

        // Fetch and store initial tokens, capture them for post-login handlers
        const tokenResult = await this._fetchInitialTokens();

        // Track login success before post-login actions
        const loginSuccessContext = AuthAnalytics.buildSigninContext({
          step: STEP_LOGIN_PAGE,
          client_id: this.ssOptions.clientId,
          tenant: this.ssOptions.tenant,
        });
        const authProvider = loginIdp?.id || DEFAULT_AUTH_PROVIDER;
        await AuthAnalytics.track(EVENT_LOGIN_SUCCESS, { auth_provider: authProvider }, loginSuccessContext);

        // Pass tokens to post-login handlers so they can include them in redirects/webhooks
        data.tokens = tokenResult;

        await this._runPostLoginAction(data);
      })
      .catch(async (e) => {
        this._handledUnexpectedException(e);
        // a reasonably good chance that we are logged in so prevent infinite login loop by forcing logout here...
        await this._logoutWithoutRedirect();
        Auth0StateHelper.resetAuthState();
        window.location = `${window.location.origin}?error=${encodeURIComponent(e.message)}`;
      });
  };

  /**
   * Fetches the initial access token (without storing - storage is delegated to post-login handlers).
   *
   * This method only fetches the tokens and returns them. The actual storage is handled by
   * post-login handlers, which can use Auth0TokenStorageHelper to store tokens according to
   * their specific needs (e.g., cookies, localStorage, webhook payload, etc.).
   *
   * @returns {Promise<{accessToken: string, refreshToken?: string, idToken?: string}>} The fetched tokens
   */
  _fetchInitialTokens = async () => {
    try {
      // Fetch access token using getTokenSilently
      // Note: getTokenSilently with detailedResponse gives us access_token and id_token but NOT refresh_token
      // The refresh token is stored in Auth0's localStorage cache but not returned in the API response
      //
      // Fetch the access token. By the time we reach here, cross-app switches have already
      // been handled by forcing loginWithRedirect in _startLogin, so we always use cache here.
      const tokenResponse = await this.providerAdapter.getTokenSilently({
        cacheMode: 'on',
        detailedResponse: true,
      });

      // Handle both detailed response (object) and simple response (string)
      const accessToken = typeof tokenResponse === 'string' ? tokenResponse : tokenResponse.access_token;
      const idToken = typeof tokenResponse === 'object' ? tokenResponse.id_token : undefined;

      // Auth0 SPA JS SDK doesn't return refresh_token in getTokenSilently response,
      // but it does store it in localStorage. We need to extract it from there.
      let refreshToken;
      try {
        // Use explicit clientId and audience to ensure we get the correct token entry
        const cachedTokenEntry = Auth0LocalStorageHelper.accessTokenEntryByClientAndAudience(
          this.ssOptions.clientId,
          this.audience,
        );
        if (cachedTokenEntry?.entryValue?.body?.refresh_token) {
          refreshToken = cachedTokenEntry.entryValue.body.refresh_token;
        }
      } catch (cacheError) {
        console.warn('_fetchInitialTokens => failed to extract refresh token from cache:', cacheError.message);
      }

      console.log(`_fetchInitialTokens => accessToken: ${accessToken?.substring(0, 20)}...`);
      if (refreshToken) {
        console.log(`_fetchInitialTokens => refreshToken: ${refreshToken?.substring(0, 20)}...`);
      } else {
        console.log('_fetchInitialTokens => no refreshToken available');
      }
      if (idToken) {
        console.log(`_fetchInitialTokens => idToken: ${idToken?.substring(0, 20)}...`);
      } else {
        console.log('_fetchInitialTokens => no idToken available');
      }

      Auth0UtilsBrowser.dumpVars();

      return { accessToken, refreshToken, idToken };
    } catch (e) {
      console.error('_fetchInitialTokens failed', e);
      this._handledUnexpectedException(e);
      throw e;
    }
  };

  _showSpinner = async (options = {}) => {
    try {
      await new Auth0WaitSpinner({
        name: options.parent || 'LoginWaitSpinner',
        waitTextOverrideKey: options.waitTextOverrideKey,
        waitTextReplacementArgs: options.waitTextReplacementArgs,
        tenantOrDomain: this.ssOptions.tenant,
        whiteLabelOptions: this._whiteLabelOptions,
      }).render();
    } catch (error) {
      logger.warn('Auth0LoginHelper spinner failed to render.', error);
    }
  };

  /**
   * @function
   * @async
   * @description Directs the `Auth0LoginHelper` to start the auth process. It will determine what phase of the login flow is happening and take appropriate action.
   * @example Loading and using the helper from an external app's HTML page
   * <script type="module">
   *  const auth0HelperModuleUrl = `https://cdn.shipstation.com/auth0/<%= auth0_tenant %>/auth0-login-helper.min.js?cb=<%=cache_buster%>`;
   *  import(auth0HelperModuleUrl).then((auth0Module) => {
   *    new auth0Module.Auth0LoginHelper(auth0Options).start();
   *  });
   * </script>
   */
  start = async () => {
    console.log('Auth0LoginHelper session start...');
    if (this._isRequestForTpPublicAsset()) {
      // prevent Auth0 load, force TP load to get to public asset
      window.location.href = this._buildForceTpRedirect();
      return;
    }
    this.sentry = await Auth0SentryReporter.reporterFactory(this.ssOptions.tenant, 'https://6e0ea9c8e7fb4591b45a8c455a5e7618@o106853.ingest.sentry.io/5433855');
    this._qs = Auth0UtilsBrowser.parseQueryString();

    if (this._hasNotificationPageErrorAtStart()) {
      this._setAndDisplayErrorOnNotificationPage(this._extractNotificationPageErrorAtStartMessage());
      return;
    }

    console.log('[WL:LoginHelper] start() _whiteLabelOptions at spinner time =>', JSON.stringify(this._whiteLabelOptions));
    console.log('[WL:LoginHelper] start() current URL =>', window.location.href);
    await this._showSpinner();

    // Only load Auth0 SPA JS SDK for Auth0 provider
    if (this.providerType === PROVIDER_AUTH0) {
      await this.scriptLoader.loadScripts(externalScriptDependencies);
    }

    if (this._qs.get('reset')) {
      console.log('Auth0LoginHelper reset detected...');
      Auth0StateHelper.resetAuthState({
        resetAuthCookieState: true,
        resetMessagingCookieState: true,
        resetLocalStorageState: true,
      });
    }

    const preLoginActionResult = await this._runPreLoginActions();
    if (preLoginActionResult) {
      // a pre-login handler was selected and handled the request
      return;
    }

    const logoutRequestType = this._isLogoutRequest();
    if (logoutRequestType) {
      console.log(`start logout request (type ${logoutRequestType.logoutType})...`);
      await this._startLogout(logoutRequestType);
      return;
    }

    console.log('start login request....');

    const firstActionPromise = this._isRefreshLoginProfileRequest()
      ? this._startLogout()
      : Promise.resolve();
    firstActionPromise
      .then(() => {
        this._setIdpContext();
        this._startLogin(this._idpContext?.id || this.auth0ConnectionRefreshIdpContext?.idpId);
      })
      .catch((e) => {
        console.error('start method firstActionPromise failed', e);
        this._handledUnexpectedException(e);
        throw e;
      });
  };
}

export { Auth0LoginHelper };
